import React from 'react';
import { CheckCircle, Leaf, TrendingDown, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Presentation = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-green-700 to-green-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Le moteur avec un buveur d'air
            </h1>
            <p className="text-xl md:text-2xl mb-8">
              Vers une combustion optimisée grâce à l'air augmenté
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="mb-12">
            <img
              src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1200&h=514&fit=crop"
              alt="Moteur écologique"
              className="w-full h-auto rounded-lg shadow-lg mb-8"
            />
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Un moteur thermique est un véritable <strong className="text-green-600">buveur d'air</strong>, 
              car l'air est le comburant essentiel à la combustion de tout type de carburant : gazole, essence, 
              hydrogène, gaz liquéfié, etc. Pour obtenir une combustion idéale, il est crucial de respecter 
              un dosage optimal entre l'air et le carburant.
            </p>
          </div>

          {/* Key Points */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
                    <Leaf className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">
                      Combustion Optimale
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Le Kit BuVeurdAir optimise le rapport air/carburant pour une combustion idéale
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
                    <TrendingDown className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">
                      Réduction de Consommation
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Économisez jusqu'à 35% de carburant grâce à une meilleure combustion
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">
                      Compatible Tous Moteurs
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Fonctionne avec essence, diesel, GPL, GNV et autres carburants
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
                    <Award className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">
                      Qualité Française
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Conçu et fabriqué en France avec des matériaux de haute qualité
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Technical Details */}
          <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Principe de Fonctionnement
            </h2>
            <div className="space-y-4 text-gray-700">
              <p className="leading-relaxed">
                Le Kit BuVeurdAir agit comme un <strong>pré-filtre intelligent</strong> qui se positionne 
                en amont du système de filtration d'air standard de votre moteur. Son rôle est triple :
              </p>
              <ul className="space-y-3 ml-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <span><strong>Collecter</strong> l'air ambiant de manière optimale</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <span><strong>Traiter</strong> et conditionner cet air pour améliorer sa qualité</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <span><strong>Optimiser</strong> le mélange air-carburant pour une combustion parfaite</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Les Avantages Concrets
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border-2 border-green-200 rounded-lg p-6 hover:border-green-400 transition-colors">
                <div className="text-3xl font-bold text-green-600 mb-2">15 à 35%</div>
                <p className="text-gray-700 font-semibold mb-2">Consommation de carburant</p>
                <p className="text-gray-600 text-sm">
                  Réduction moyenne constatée sur l'ensemble des véhicules testés
                </p>
              </div>
              <div className="bg-white border-2 border-green-200 rounded-lg p-6 hover:border-green-400 transition-colors">
                <div className="text-4xl font-bold text-green-600 mb-2">-30%</div>
                <p className="text-gray-700 font-semibold mb-2">Émissions polluantes</p>
                <p className="text-gray-600 text-sm">
                  Diminution significative des émissions de CO2 et particules fines
                </p>
              </div>
              <div className="bg-white border-2 border-green-200 rounded-lg p-6 hover:border-green-400 transition-colors">
                <div className="text-4xl font-bold text-green-600 mb-2">+15%</div>
                <p className="text-gray-700 font-semibold mb-2">Performances moteur</p>
                <p className="text-gray-600 text-sm">
                  Amélioration du couple et de la réactivité du moteur
                </p>
              </div>
            </div>
          </div>

          {/* Installation */}
          <div className="bg-gray-50 rounded-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Installation Simple et Rapide
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Le Kit BuVeurdAir a été conçu pour une installation facile, sans modification du moteur. 
              En moyenne, la pose prend <strong className="text-green-600">30 à 60 minutes</strong> selon 
              le type de véhicule.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Aucune compétence technique particulière n'est requise, mais nous recommandons de confier 
              l'installation à un professionnel pour garantir une efficacité optimale.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Presentation;